/**
 * The attempt drawer's "Benchmark diagnostics" section (components/registry/
 * BenchDiagnostics.tsx): the RAW detail that must never be public. Two
 * sources, both internal and operator-authenticated:
 *
 *  - the attempt's own `steps` checklist (publish_versions.steps): every
 *    degraded / failed step with its FULL reason text and each benchmark
 *    view's reason (the banner above the drawer only shows the first line,
 *    cut at 400 chars);
 *  - GET /v1/publish/versions/<pkg>/<version>/diagnostics (broker
 *    handleVersionDiagnostics): the stored bench results' per-variant
 *    (glibc / musl / macOS / fixtures) messages, per-arm failure text with
 *    exit code and first stderr lines, output-diff snippets and the hosts
 *    that ran it. The public registry never receives any of it.
 */
import { parseSteps, problemSteps, STATE_LABEL } from "./stepChecklist.ts";

/** DOM id of an attempt's "Benchmark diagnostics" section (the step panel's
 *  "View diagnostics" button opens it). */
export function diagnosticsAnchorId(attemptKey: string): string {
  return `bench-diagnostics-${attemptKey.replace(/[^A-Za-z0-9_-]/g, "_")}`;
}

/** A degraded / failed benchmark step (fixtures, glibc, musl, macOS or the
 *  aggregate) links to the raw diagnostics: its row text is only the start. */
export function offersDiagnostics(stepKey: string, state: string): boolean {
  return (
    stepKey.startsWith("benchmark") &&
    (state === "degraded" || state === "failed")
  );
}

export interface BenchDiagnosticItem {
  variant: string;
  where: string;
  kind: string;
  text: string;
}

export interface BenchDiagnosticsRun {
  id: number;
  compilerVersion: string;
  publishedAs: string | null;
  status: string;
  createdAt: string;
  publishHost: string | null;
  runId: string | null;
  rev: number | null;
  macosHosts: string[];
  host: Record<string, unknown> | null;
  variants: Array<{ variant: string; status: string; reason: string | null }>;
  items: BenchDiagnosticItem[];
}

export interface BenchDiagnosticsResponse {
  ok: boolean;
  name: string;
  version: string;
  runs: BenchDiagnosticsRun[];
}

/** One block of step text: "<step> DEGRADED" + the whole reason. */
export interface StepNote {
  title: string;
  text: string;
}

/** Every degraded / failed step of the attempt (and each benchmark view with
 *  a reason) with the complete, untruncated text. */
export function stepNotes(steps: unknown): StepNote[] {
  const parsed = parseSteps(steps);
  if (!parsed) return [];
  const out: StepNote[] = [];
  for (const s of problemSteps(parsed)) {
    const reason = (s.reason ?? "").trim();
    out.push({
      title: `${s.name} ${STATE_LABEL[s.state]}`,
      text: reason || "(no reason recorded)",
    });
  }
  for (const s of parsed)
    for (const v of s.views ?? [])
      if (v.reason && v.reason.trim() && v.state !== "ok")
        out.push({
          title: `${s.name} · ${v.libc} · ${v.runtime} ${v.state}`,
          text: v.reason.trim(),
        });
  return out;
}

const VARIANT_ORDER = ["glibc", "musl", "macos", "fixtures", "run"];

/** Items grouped by variant in a fixed display order. */
export function groupByVariant(
  items: readonly BenchDiagnosticItem[],
): Array<{ variant: string; items: BenchDiagnosticItem[] }> {
  const by = new Map<string, BenchDiagnosticItem[]>();
  for (const i of items) {
    const list = by.get(i.variant) ?? [];
    list.push(i);
    by.set(i.variant, list);
  }
  const rank = (v: string) => {
    const k = VARIANT_ORDER.indexOf(v);
    return k < 0 ? VARIANT_ORDER.length : k;
  };
  return [...by.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([variant, list]) => ({ variant, items: list }));
}

export function variantLabel(v: string): string {
  switch (v) {
    case "glibc":
      return "Linux (glibc)";
    case "musl":
      return "Alpine (musl)";
    case "macos":
      return "macOS arm64";
    case "fixtures":
      return "Fixtures / native builds";
    default:
      return v === "run" ? "Run" : v;
  }
}

/** The plain-text export behind the section's "Copy all" button. */
export function diagnosticsText(
  notes: readonly StepNote[],
  runs: readonly BenchDiagnosticsRun[],
): string {
  const out: string[] = [];
  for (const n of notes) out.push(`## ${n.title}`, n.text, "");
  for (const r of runs) {
    out.push(
      `## bench run #${r.id} (${r.status}) compiler ${r.compilerVersion} ${r.createdAt}`,
      `publish host: ${r.publishHost ?? "?"}${r.macosHosts.length ? ` · macOS: ${r.macosHosts.join(", ")}` : ""}`,
    );
    for (const v of r.variants)
      out.push(`${v.variant}: ${v.status}${v.reason ? ` — ${v.reason}` : ""}`);
    for (const g of groupByVariant(r.items))
      for (const i of g.items)
        out.push(``, `[${g.variant}] ${i.where} · ${i.kind}`, i.text);
    out.push("");
  }
  return out.join("\n").trimEnd();
}
